import { Alert, Button, Drawer, Input, Progress, Typography } from "antd";
import { CheckOutlined, LoadingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import { useWorkspace } from "contexts/WorkspaceContext";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const GENERATION_STEPS = [
  "Preparing role context",
  "Drafting job description",
  "Structuring requirements",
  "Getting draft ready",
];

const sectionView = (label, items) => {
  if (!items || !items.length) return null;
  return (
    <div className="ai-draft-section">
      <Text strong>{label}</Text>
      <ul>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

export default function AiJobDescriptionModal({
  open,
  prompt,
  onPromptChange,
  generating,
  draft,
  error,
  onGenerate,
  onApply,
  onClose,
  usage,
}) {
  const navigate = useNavigate();
  const { selectedWorkspace } = useWorkspace();
  const workspaceId = selectedWorkspace?.id;

  const hasReachedLimit = usage?.hasReachedLimit ?? false;
  const limit = usage?.limit;
  const remaining = usage?.remaining;
  const planCode = usage?.planCode;

  const isPremium = planCode === "PREMIUM";
  const isStandard = planCode === "STANDARD";
  const planName = planCode
    ? planCode.charAt(0).toUpperCase() + planCode.slice(1).toLowerCase()
    : "Free";

  const getPlanBadgeStyle = () => {
    if (isPremium) return { bg: "#f3e8ff", border: "#d8b4fe", text: "#7c3aed" };
    if (isStandard) return { bg: "#eff6ff", border: "#bfdbfe", text: "#2563eb" };
    return { bg: "#f0fdf4", border: "#bbf7d0", text: "#16a34a" };
  };

  return (
    <Drawer
      title={
        <span>
          <ThunderboltOutlined style={{ marginRight: 8 }} />
          AI Job Description Generator
        </span>
      }
      open={open}
      onClose={onClose}
      width={640}
      footer={null}
      destroyOnClose
      className="ai-job-description-modal"
    >
      {!draft ? (
        <>
          {hasReachedLimit ? (
            <div>
              <Alert
                type="warning"
                message={`You've used all ${limit} AI requests this period`}
                description={
                  <span>
                    {isStandard
                      ? `Upgrade to Premium for unlimited AI generations.`
                      : `Upgrade to Standard for more requests/month or Premium for unlimited AI generations.`}
                    <br />
                    <Button type="link" onClick={() => navigate(paths.workspaceSubscriptions(workspaceId))}>
                      View Plans &rarr;
                    </Button>
                  </span>
                }
                showIcon
              />
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <Paragraph type="secondary">
                  Describe the role you want to create. The AI will use any details already filled in
                  on the form (title, skills, experience, etc.) as context. You can review and edit the
                  result before applying it.
                </Paragraph>

                {!hasReachedLimit && limit !== null && remaining !== null && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      marginBottom: 12,
                      padding: "12px 14px",
                      background: "#fafbfc",
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                    }}
                  >
                    {/* Top row: plan badge + upgrade button */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "3px 10px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            background: getPlanBadgeStyle().bg,
                            border: `1px solid ${getPlanBadgeStyle().border}`,
                            color: getPlanBadgeStyle().text,
                          }}
                        >
                          <ThunderboltOutlined style={{ fontSize: 10 }} />
                          {planName}
                        </span>
                        <Text style={{ fontSize: 12, color: "#6b7280" }}>
                          <Text strong style={{ fontSize: 13, color: "#374151" }}>{remaining}</Text>
                          <span style={{ margin: "0 2px" }}>/</span>
                          <Text style={{ color: "#9ca3af" }}>{limit}</Text>
                          <span style={{ marginLeft: 4 }}>requests left</span>
                        </Text>
                      </div>
                      {!isPremium && (
                        <Button
                          type="default"
                          size="small"
                          onClick={() => navigate(paths.workspaceSubscriptions(workspaceId))}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 6,
                            height: 30,
                          }}
                        >
                          Upgrade
                        </Button>
                      )}
                    </div>

                    {/* Progress bar */}
                    <Progress
                      percent={Math.round(((limit - remaining) / limit) * 100)}
                      showInfo={false}
                      size="small"
                      strokeColor={remaining <= 5 ? "#f59e0b" : remaining <= 1 ? "#ef4444" : "#22c55e"}
                      trailColor="#e5e7eb"
                    />
                  </div>
                )}

                <TextArea
                  value={prompt}
                  onChange={(event) => onPromptChange(event.target.value)}
                  placeholder='e.g. "Create a job description for a Senior Backend Java Developer with Spring Boot experience"'
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  disabled={generating}
                />
              </div>

              {generating ? (
                <div className="ai-generation-progress" aria-live="polite">
                  <div className="ai-generation-progress__header">
                    <span className="ai-generation-progress__icon">
                      <LoadingOutlined />
                    </span>
                    <div>
                      <Text className="ai-generation-progress__title">Generating your job description</Text>
                      <Paragraph className="ai-generation-progress__tagline">
                        This can take a moment while AI uses your role details, skills, and workspace plan to prepare a draft.
                      </Paragraph>
                    </div>
                  </div>
                  <div className="ai-generation-progress__steps">
                    {GENERATION_STEPS.map((label, index) => (
                      <div key={label} className="ai-generation-progress__step">
                        <span className="ai-generation-progress__step-icon">
                          {index === 0 ? <CheckOutlined /> : index === 1 ? <LoadingOutlined /> : index + 1}
                        </span>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {error ? (
                <div className="ai-draft-error">
                  <Text type="danger">{error}</Text>
                </div>
              ) : null}

              <div className="ai-modal-actions">
                <Button onClick={onClose} disabled={generating}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={onGenerate}
                  loading={generating}
                  disabled={!prompt.trim() || generating || hasReachedLimit}
                >
                  {generating ? "Generating..." : "Generate"}
                </Button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="ai-draft-preview">
            <div className="ai-draft-meta">
              <Text type="secondary" className="ai-draft-meta-text">
                {draft.aiGenerated ? "AI-generated" : "Draft"}
              </Text>
            </div>

            {draft.title ? (
              <div className="ai-draft-section">
                <Text strong>Title</Text>
                <Paragraph>{draft.title}</Paragraph>
              </div>
            ) : null}

            {draft.summary ? (
              <div className="ai-draft-section">
                <Text strong>Summary</Text>
                <Paragraph>{draft.summary}</Paragraph>
              </div>
            ) : null}

            {draft.description ? (
              <div className="ai-draft-section">
                <Text strong>Description</Text>
                <Paragraph>{draft.description}</Paragraph>
              </div>
            ) : null}

            {sectionView("Responsibilities", draft.responsibilities)}
            {sectionView("Requirements", draft.requirements)}
            {sectionView("Preferred Qualifications", draft.preferredQualifications)}
            {sectionView("Benefits", draft.benefits)}
            {sectionView("Required Skills", draft.requiredSkills)}
            {sectionView("Soft Skills", draft.softSkills)}
          </div>

          <div className="ai-modal-actions" style={{ marginTop: 16 }}>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={onApply}>
              Apply to Form
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
}
